/**
 * SEO Co-Pilot dashboard — the custom admin tab.
 *
 * Three sections, gated by plan:
 *   1. Latest audit (Free+) — score, counts, top 5 findings, "Run audit" button.
 *   2. Directory recommendations (Pro+) — table with Add-URL deep links.
 *   3. Blog draft + outreach (Business) — one-click generators.
 *
 * The Phase 4 GBP wizard surfaces here too: a card that either offers
 * "Connect Google Business Profile" (when on Pro+ and creds configured) or
 * shows the connection status if already connected. Approval-pending
 * cases get a polite "We're still waiting on Google's API approval"
 * message rather than a broken button.
 */
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Loader2,
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { describeIndexQueueRun } from '@/lib/seo-queue-run';
import { apiHelpers } from '@/lib/api';
import { SearchEnginesCard, useSearchEngines } from '@/components/seo/SearchEnginesCard';

interface AuditCounts {
  info: number;
  warning: number;
  critical: number;
  passed: number;
}
interface AuditFinding {
  code: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  remedy?: string;
}
interface AuditRow {
  ranAt: string;
  score: number;
  counts: AuditCounts;
  findings: AuditFinding[];
}

// Findings the Business-tier edge remediation fixes automatically. Mirrors
// AUTO_FIXABLE_AUDIT_CODES in lib/seo/head-remediation.ts.
const AUTO_FIXABLE_CODES = new Set([
  'canonical-missing',
  'meta-description-missing',
  'og-incomplete',
  'viewport-missing',
  'html-lang-missing',
]);

/**
 * The audit inspects the LIVE page — i.e. after Autopilot's edge pass. A
 * finding that still shows up therefore was NOT fixed. The one code where
 * that happens routinely is og-incomplete with og:image: Autopilot can only
 * source it from a brand-kit logo. Never show the badge for that case.
 */
function isAutoFixed(f: AuditFinding): boolean {
  if (!AUTO_FIXABLE_CODES.has(f.code)) return false;
  if (f.code === 'og-incomplete' && /og:image/i.test(f.message)) return false;
  return true;
}

function severityIcon(s: AuditFinding['severity']) {
  switch (s) {
    case 'critical':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
}

function scoreColor(score: number) {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Core Web Vitals metric metadata — Google's official "Good / Needs improvement
 * / Poor" thresholds (web.dev/vitals) + a plain-English explanation. Values are
 * ms for time metrics, unitless for CLS.
 */
const CWV_META: Record<string, { name: string; explain: string; good: number; poor: number }> = {
  LCP: {
    name: 'Largest Contentful Paint',
    explain: 'How long until the main content of the page is visible. Loading speed.',
    good: 2500,
    poor: 4000,
  },
  INP: {
    name: 'Interaction to Next Paint',
    explain: 'How quickly the page reacts after a tap or click. Responsiveness.',
    good: 200,
    poor: 500,
  },
  CLS: {
    name: 'Cumulative Layout Shift',
    explain: 'How much the page unexpectedly jumps around while loading. Visual stability.',
    good: 0.1,
    poor: 0.25,
  },
  FCP: {
    name: 'First Contentful Paint',
    explain: 'When the first text or image appears on screen.',
    good: 1800,
    poor: 3000,
  },
  TTFB: {
    name: 'Time to First Byte',
    explain: 'How fast the server starts responding to the request.',
    good: 800,
    poor: 1800,
  },
};

function cwvRating(id: string, value: number): { label: string; cls: string } | null {
  const meta = CWV_META[id];
  if (!meta) return null;
  if (value <= meta.good) return { label: 'Good', cls: 'text-green-600' };
  if (value <= meta.poor) return { label: 'Needs improvement', cls: 'text-amber-600' };
  return { label: 'Poor', cls: 'text-red-600' };
}

// ── Index queue: one cell per search engine ─────────────────────────────────
// Per-row state is `engines.google` / `engines.bing` (lib/seo/types.ts
// `SeoEngineIndexState`). Rows written before 2026-10-15 carry only the legacy
// top-level Google fields; `legacyGoogleStatus` reads those so the column is
// never blank during the rollout.
type EngineCellState = {
  status: 'unknown' | 'submitted' | 'indexed' | 'not_indexed' | 'error';
  lastCheckedAt?: string | Date;
  lastSubmittedAt?: string | Date;
  submitCount?: number;
  detail?: string;
};

function legacyGoogleStatus(row: any): EngineCellState['status'] {
  if (row.status === 'indexed') return 'indexed';
  if (row.status === 'tracking') return 'not_indexed';
  if (row.status === 'error') return 'error';
  return row.submittedAt ? 'submitted' : 'unknown';
}

const ENGINE_STATUS_LABEL: Record<EngineCellState['status'], { text: string; cls: string }> = {
  indexed: { text: 'Indexed', cls: 'text-green-600' },
  not_indexed: { text: 'Not indexed', cls: 'text-amber-600' },
  submitted: { text: 'Submitted', cls: 'text-sky-600' },
  error: { text: 'Error', cls: 'text-red-600' },
  unknown: { text: 'Not checked', cls: 'text-muted-foreground' },
};

function fmtDay(v?: string | Date): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function EngineCell({ state, submitLabel }: { state: EngineCellState; submitLabel: string }) {
  const meta = ENGINE_STATUS_LABEL[state.status] ?? ENGINE_STATUS_LABEL.unknown;
  return (
    <div className="min-w-[120px]">
      <div className={`text-xs font-medium ${meta.cls}`} title={state.detail || undefined}>
        {meta.text}
      </div>
      <div className="text-muted-foreground text-[11px]">
        {state.lastCheckedAt ? `Checked ${fmtDay(state.lastCheckedAt)}` : 'Never checked'}
      </div>
      <div className="text-muted-foreground text-[11px]">
        {state.lastSubmittedAt
          ? `${submitLabel} ${fmtDay(state.lastSubmittedAt)}${
              state.submitCount && state.submitCount > 1 ? ` (×${state.submitCount})` : ''
            }`
          : 'Not yet submitted'}
      </div>
    </div>
  );
}

function UpgradeChip({ to }: { to: 'pro' | 'business' }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      <Crown className="h-3 w-3" /> Requires {to.charAt(0).toUpperCase() + to.slice(1)}
    </span>
  );
}

export default function SeoCopilotDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch manifest so we know the plan.
  const { data: manifestData } = useQuery({
    queryKey: ['plugin-manifest', projectId, 'seo-copilot'],
    queryFn: () => apiHelpers.getPluginManifest(projectId!, 'seo-copilot'),
    enabled: Boolean(projectId),
  });
  const installed = manifestData?.data?.data?.installed;
  const plan: string = (installed?.config?.plan as string) || 'free';
  const isStarter = plan !== 'free';
  const isPro = plan === 'pro' || plan === 'business';
  const isBusiness = plan === 'business';

  // Audits.
  const { data: auditsData } = useQuery({
    queryKey: ['seo-audits', projectId],
    queryFn: () => apiHelpers.seoCopilotAudits(projectId!),
    enabled: Boolean(projectId) && Boolean(installed),
  });
  const audits: AuditRow[] = auditsData?.data?.data?.audits || [];
  const latest: AuditRow | null = audits[0] || null;

  const runAuditMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotRunAudit(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-audits', projectId] });
      toast.success('Audit complete');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Audit failed');
    },
  });

  // OTA recs (Pro+).
  const { data: otaData } = useQuery({
    queryKey: ['seo-ota', projectId],
    queryFn: () => apiHelpers.seoCopilotOta(projectId!),
    enabled: Boolean(projectId) && isPro,
  });
  const recs = otaData?.data?.data?.recommendations || [];

  // ── Core Web Vitals (Starter+) ──────────────────────────────
  const { data: cwvData } = useQuery({
    queryKey: ['seo-cwv', projectId],
    queryFn: () => apiHelpers.seoCopilotCwv(projectId!),
    enabled: Boolean(projectId) && Boolean(installed) && isStarter,
  });
  const cwvLatest: any = cwvData?.data?.data?.latest || null;
  const cwvAvailable: boolean = Boolean(cwvData?.data?.data?.available);
  const refreshCwvMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotRefreshCwv(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-cwv', projectId] });
      toast.success('Core Web Vitals refreshed');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Refresh failed'),
  });

  // ── Search engines (Pro+) — one query feeds the card AND gates the reports.
  const { data: engines } = useSearchEngines(projectId, Boolean(installed) && isPro);
  const gscConnected: boolean = engines?.google.status === 'connected';
  const { data: gscReportsData } = useQuery({
    queryKey: ['seo-gsc-reports', projectId],
    queryFn: () => apiHelpers.seoCopilotGscReports(projectId!),
    enabled: Boolean(projectId) && isPro && gscConnected,
  });
  const gscQueries: any[] = gscReportsData?.data?.data?.queries || [];

  // ── Index queue (Pro+, at least one engine connected) ───────
  const bingConnected: boolean = engines?.bing?.status === 'connected';
  const { data: indexQueueData } = useQuery({
    queryKey: ['seo-index-queue', projectId],
    queryFn: () => apiHelpers.seoCopilotIndexQueue(projectId!),
    enabled: Boolean(projectId) && isPro && (gscConnected || bingConnected),
  });
  const indexQueue: any[] = indexQueueData?.data?.data?.queue || [];
  const indexedCount: number = indexQueueData?.data?.data?.indexedCount || 0;
  // Paths of every live URL no scanned page links to — Google-indexed rows
  // included, which the active `queue` list leaves out.
  const orphanPaths: string[] = indexQueueData?.data?.data?.orphans || [];
  const dailyBudget: number = indexQueueData?.data?.data?.dailyInspectionBudget || 0;
  const inspectedToday: number = indexQueueData?.data?.data?.inspectedToday || 0;
  // Per-engine counts (server-computed; Google = inspect-only, Bing = inspect + submit).
  const queueEngines: {
    google?: { connected: boolean; indexed: number };
    bing?: { connected: boolean; indexed: number; notIndexed: number; submitted?: number };
  } = indexQueueData?.data?.data?.engines || {};
  const showGoogleCol = gscConnected || Boolean(queueEngines.google?.connected);
  const showBingCol = bingConnected || Boolean(queueEngines.bing?.connected);
  // What the hourly auto-submit cron last did (lib/seo/index-submit.ts).
  const autoSubmit: {
    lastRunAt?: string;
    urlsInSitemap?: number;
    lastSitemapSubmitAt?: string;
    lastSitemapSubmitOk?: boolean;
    lastIndexNowAt?: string;
    lastIndexNowOk?: boolean;
    lastIndexNowUrls?: number;
    lastError?: string;
    lastBingCheckAt?: string;
    lastBingSubmitAt?: string;
    lastBingSubmitOk?: boolean;
    lastBingSubmitUrls?: number;
    bingDailyQuota?: number;
    lastBingError?: string;
    linkScanCoverage?: { scanned: number; total: number };
    orphanCount?: number;
  } | null = indexQueueData?.data?.data?.autoSubmit || null;
  // ── Google "Request indexing" worklist (Pro+, GSC connected) ──
  const { data: worklistData } = useQuery({
    queryKey: ['seo-request-worklist', projectId],
    queryFn: () => apiHelpers.seoCopilotRequestWorklist(projectId!),
    enabled: Boolean(projectId) && isPro && gscConnected,
  });
  const worklist: {
    items: Array<{
      url: string;
      coverageState: string;
      kind?: 'not_indexed' | 'unknown';
      sitemapTier: 0 | 1 | 2;
      impressions: number;
      inspectUrl: string;
    }>;
    candidates: number;
    requestedToday: number;
    dailyLimit: number;
    rotationDays: number;
    hasImpressions: boolean;
  } | null = worklistData?.data?.data || null;
  const markRequestedMutation = useMutation({
    mutationFn: (url: string) => apiHelpers.seoCopilotMarkRequested(projectId!, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-request-worklist', projectId] });
      queryClient.invalidateQueries({ queryKey: ['seo-index-queue', projectId] });
      toast.success('Marked as requested — it will be re-inspected on the next hourly run');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Could not mark as requested'),
  });
  const { data: rankData } = useQuery({
    queryKey: ['seo-rank-tracking', projectId],
    queryFn: () => apiHelpers.seoCopilotRankTracking(projectId!),
    enabled: Boolean(projectId) && isPro && gscConnected,
  });
  const rankKeywords: any[] = rankData?.data?.data?.keywords || [];
  const reconcileQueueMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotReconcileIndexQueue(projectId!),
    onSuccess: (res: any) => {
      const d = res?.data?.data;
      queryClient.invalidateQueries({ queryKey: ['seo-index-queue', projectId] });
      if (!d) {
        toast.success('Queue updated');
        return;
      }
      const { summary, warning } = describeIndexQueueRun(d);
      toast.success(summary, { duration: 8000 });
      if (warning) toast.warning(warning, { duration: 10000 });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Queue run failed'),
  });

  if (!projectId) return null;

  return (
    <div className="space-y-6 p-6">
      {/* Back */}
      <button
        onClick={() => navigate(`/projects/${projectId}/plugins/seo-copilot`)}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to SEO Co-Pilot
      </button>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">SEO Co-Pilot Dashboard</h1>
        <button
          onClick={() => navigate(`/projects/${projectId}/plugins/seo-copilot/autopilot`)}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <Sparkles className="h-4 w-4" /> SEO Autopilot
        </button>
      </div>

      {/* ── 1. Audit ─────────────────────────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Latest audit</h2>
          <button
            onClick={() => runAuditMutation.mutate()}
            disabled={runAuditMutation.isPending}
            className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {runAuditMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Run audit
          </button>
        </div>

        {!latest && (
          <p className="text-muted-foreground text-sm">
            No audit yet. Click <em>Run audit</em> above to scan your storefront.
          </p>
        )}

        {latest && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground text-xs uppercase">Score</div>
              <div className={`text-3xl font-bold ${scoreColor(latest.score)}`}>{latest.score}</div>
              <div className="text-muted-foreground text-xs">
                Last run {new Date(latest.ranAt).toLocaleString()}
              </div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground text-xs uppercase">Findings</div>
              <div className="mt-1 space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  <span>{latest.counts.critical} critical</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  <span>{latest.counts.warning} warnings</span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="h-3.5 w-3.5 text-blue-500" />
                  <span>{latest.counts.info} info</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  <span>{latest.counts.passed} passed</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg border p-4 md:col-span-1">
              <div className="text-muted-foreground text-xs uppercase">Top findings</div>
              {latest.findings.length === 0 ? (
                <div className="mt-2 text-sm text-green-600">All checks passing 🎉</div>
              ) : (
                <ul className="mt-2 space-y-2 text-sm">
                  {latest.findings.slice(0, 5).map((f) => (
                    <li key={f.code} className="flex items-start gap-2">
                      {severityIcon(f.severity)}
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{f.message}</span>
                          {isBusiness && isAutoFixed(f) && (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Auto-fixed by Autopilot
                            </span>
                          )}
                        </div>
                        {f.remedy && (
                          <div className="text-muted-foreground mt-0.5 text-xs">{f.remedy}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {!isStarter && latest && latest.findings.length > 5 && (
          <p className="text-muted-foreground mt-3 text-xs">
            Showing top 5. Upgrade to Starter for daily audits and full history.{' '}
            <Link
              to={`/projects/${projectId}/plugins/seo-copilot?tab=plans`}
              className="text-primary underline"
            >
              See plans →
            </Link>
          </p>
        )}
      </section>

      {/* ── Core Web Vitals (Starter+) ──────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Core Web Vitals</h2>
          <div className="flex items-center gap-2">
            {!isStarter && <UpgradeChip to="pro" />}
            {isStarter && cwvAvailable && (
              <button
                onClick={() => refreshCwvMutation.mutate()}
                disabled={refreshCwvMutation.isPending}
                className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {refreshCwvMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Refresh
              </button>
            )}
          </div>
        </div>
        {!isStarter && (
          <p className="text-muted-foreground text-sm">
            Upgrade to Starter to monitor your real Largest Contentful Paint, Interaction to Next
            Paint, and Cumulative Layout Shift — the field metrics Google ranks on.
          </p>
        )}
        {isStarter && !cwvLatest && (
          <p className="text-muted-foreground text-sm">
            No measurement yet. Click <em>Refresh</em> to pull live performance data from Google
            PageSpeed.
          </p>
        )}
        {isStarter && cwvLatest && (
          <div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {(cwvLatest.metrics || []).map((m: any) => {
                const meta = CWV_META[m.id];
                const rating = cwvRating(m.id, m.value);
                const fmtThresh = (v: number) =>
                  m.unit === 'ms' ? `${(v / 1000).toFixed(1)}s` : v;
                return (
                  <div
                    key={m.id}
                    className="rounded-lg border p-3"
                    title={
                      meta
                        ? `${meta.name}\n${meta.explain}\nGood ≤ ${fmtThresh(meta.good)} · Poor > ${fmtThresh(meta.poor)}`
                        : m.id
                    }
                  >
                    <div className="text-muted-foreground flex items-center gap-1 text-xs font-medium uppercase">
                      {m.id}
                      <Info className="h-3 w-3 opacity-50" />
                    </div>
                    <div className="mt-0.5 text-xl font-bold">
                      {m.unit === 'ms'
                        ? m.value >= 1000
                          ? `${(m.value / 1000).toFixed(1)} s`
                          : `${Math.round(m.value)} ms`
                        : m.value.toFixed(2)}
                    </div>
                    {rating && (
                      <div className={`text-xs font-semibold ${rating.cls}`}>{rating.label}</div>
                    )}
                    {meta && (
                      <div className="text-muted-foreground mt-1 text-[11px] leading-snug">
                        {meta.explain}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500" /> Good
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Needs
                improvement
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Poor
              </span>
              <span className="ml-auto">
                Source{' '}
                {cwvLatest.source === 'field' ? 'real-user field data (CrUX)' : 'lab (Lighthouse)'}
                {cwvLatest.performanceScore != null &&
                  ` · performance ${cwvLatest.performanceScore}/100`}
                {' · '}
                {new Date(cwvLatest.ranAt).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* ── Search engines (Pro+): connect, verify, register, submit — one button each ── */}
      <section className="bg-card rounded-xl border p-5">
        <SearchEnginesCard projectId={projectId} isPro={isPro} />
        {isPro && gscConnected && (
          <div className="mt-6 border-t pt-4">
            <div className="text-sm font-medium">Top queries (last 28 days)</div>
            {gscQueries.length === 0 && (
              <p className="text-muted-foreground mt-1 text-sm">
                No query data yet — Search Console needs a few days of impressions.
              </p>
            )}
            {gscQueries.length > 0 && (
              <table className="mt-2 w-full text-left text-sm">
                <thead className="text-muted-foreground text-xs uppercase">
                  <tr>
                    <th className="pb-2 pr-4">Query</th>
                    <th className="pb-2 pr-4">Clicks</th>
                    <th className="pb-2 pr-4">Impr.</th>
                    <th className="pb-2">Avg pos.</th>
                  </tr>
                </thead>
                <tbody>
                  {gscQueries.slice(0, 15).map((q: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-4">{q.key}</td>
                      <td className="py-1.5 pr-4">{q.clicks}</td>
                      <td className="py-1.5 pr-4">{q.impressions}</td>
                      <td className="py-1.5">{q.position?.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Rank tracking */}
            {rankKeywords.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <div className="mb-1 text-sm font-medium">Keyword rankings</div>
                <p className="text-muted-foreground mb-2 text-xs">
                  Average Google position for your target keywords, tracked weekly. ▲ = moved up.
                </p>
                <table className="w-full text-left text-sm">
                  <thead className="text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="pb-2 pr-4">Keyword</th>
                      <th className="pb-2 pr-4">Position</th>
                      <th className="pb-2">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankKeywords.slice(0, 20).map((k: any) => (
                      <tr key={k.keyword} className="border-t">
                        <td className="max-w-[280px] truncate py-1.5 pr-4" title={k.keyword}>
                          {k.keyword}
                        </td>
                        <td className="py-1.5 pr-4">
                          {k.latest?.position != null ? `#${k.latest.position}` : 'Not ranking'}
                        </td>
                        <td className="py-1.5">
                          {k.delta == null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : k.delta > 0 ? (
                            <span className="text-green-600">▲ {k.delta}</span>
                          ) : k.delta < 0 ? (
                            <span className="text-red-600">▼ {Math.abs(k.delta)}</span>
                          ) : (
                            <span className="text-muted-foreground">no change</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Google "Request indexing" worklist */}
            {gscConnected && worklist && (
              <div className="mt-6 border-t pt-4">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-sm font-medium">Request in Google today</div>
                  <span className="text-muted-foreground text-xs">
                    {worklist.requestedToday}/{worklist.dailyLimit} marked today
                  </span>
                </div>
                <p className="text-muted-foreground mb-2 text-xs">
                  Google allows about {worklist.dailyLimit} manual &ldquo;Request indexing&rdquo;
                  clicks per property per day and offers no API for it on store or blog pages. The
                  plugin cannot press the button for you, but it ranks the {worklist.candidates} URL
                  {worklist.candidates === 1 ? '' : 's'} Google has seen and not indexed or does not
                  know at all (seen-and-declined first, then core pages, then pages already earning
                  impressions), deep-links each one into Search Console, and re-inspects it after
                  you request. A requested URL leaves this list for {worklist.rotationDays} days.
                </p>
                {worklist.items.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Nothing to request right now — every not-indexed or unknown URL was requested in
                    the last {worklist.rotationDays} days, or Google has not reported any as
                    &ldquo;currently not indexed&rdquo; or &ldquo;unknown to Google&rdquo;.
                  </p>
                ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="text-muted-foreground text-xs uppercase">
                      <tr>
                        <th className="pb-2 pr-4">#</th>
                        <th className="pb-2 pr-4">URL</th>
                        <th className="pb-2 pr-4">Why</th>
                        <th className="pb-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {worklist.items.map((item, i) => (
                        <tr key={item.url} className="border-t align-top">
                          <td className="text-muted-foreground py-1.5 pr-4 text-xs">{i + 1}</td>
                          <td className="max-w-[280px] truncate py-1.5 pr-4" title={item.url}>
                            {item.url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                          </td>
                          <td className="text-muted-foreground py-1.5 pr-4 text-xs">
                            <div>{item.coverageState}</div>
                            <div className="text-[11px]">
                              {item.sitemapTier === 0
                                ? 'Core page'
                                : item.sitemapTier === 2
                                  ? 'Content page'
                                  : 'Page'}
                              {item.impressions > 0
                                ? ` · ${item.impressions} impressions in 28 days`
                                : ''}
                            </div>
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <a
                                href={item.inspectUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                              >
                                Open in Search Console <ExternalLink className="h-3 w-3" />
                              </a>
                              <button
                                onClick={() => markRequestedMutation.mutate(item.url)}
                                disabled={markRequestedMutation.isPending}
                                className="hover:bg-muted inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs disabled:opacity-50"
                              >
                                <CheckCircle2 className="h-3 w-3" /> Mark requested
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Index queue */}
            <div className="mt-6 border-t pt-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-sm font-medium">Index queue</div>
                <button
                  onClick={() => reconcileQueueMutation.mutate()}
                  disabled={reconcileQueueMutation.isPending}
                  className="text-primary inline-flex items-center gap-1 text-xs hover:underline disabled:opacity-50"
                >
                  {reconcileQueueMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Check now
                </button>
              </div>
              <p className="text-muted-foreground mb-2 text-xs">
                Every hour your sitemap is re-read and every URL is tracked per search engine.
                Google has no per-URL &ldquo;request indexing&rdquo; API for store or blog pages, so
                for Google the plugin re-submits your sitemap, inspects each URL and shows the fix
                when one is needed. Bing has both inspection and per-URL submission, so unindexed
                URLs are pushed to Bing automatically within its daily quota.{' '}
                {showGoogleCol ? `${indexedCount} indexed in Google` : ''}
                {showGoogleCol && showBingCol ? ' · ' : ''}
                {showBingCol ? `${queueEngines.bing?.indexed ?? 0} in Bing` : ''}
                {showBingCol &&
                ((queueEngines.bing?.notIndexed ?? 0) > 0 ||
                  (queueEngines.bing?.submitted ?? 0) > 0)
                  ? ` (${[
                      (queueEngines.bing?.notIndexed ?? 0) > 0
                        ? `${queueEngines.bing?.notIndexed} checked, not in Bing yet`
                        : '',
                      (queueEngines.bing?.submitted ?? 0) > 0
                        ? `${queueEngines.bing?.submitted} submitted, awaiting Bing's crawl`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')})`
                  : ''}
                {showGoogleCol
                  ? ` · ${inspectedToday}/${dailyBudget} Google checks used today`
                  : ''}
                {showBingCol && typeof autoSubmit?.bingDailyQuota === 'number'
                  ? ` · ${autoSubmit.bingDailyQuota} Bing submissions left today`
                  : ''}
                {typeof autoSubmit?.orphanCount === 'number'
                  ? ` · ${autoSubmit.orphanCount} orphan URL${autoSubmit.orphanCount === 1 ? '' : 's'} (no internal link)`
                  : ''}
                .
              </p>
              {orphanPaths.length > 0 && (
                <details className="mb-2 text-xs">
                  <summary className="text-muted-foreground cursor-pointer">
                    {orphanPaths.length} page{orphanPaths.length === 1 ? ' has' : 's have'} no
                    internal link — add a link from a menu, footer or related page so crawlers (and
                    visitors) can reach {orphanPaths.length === 1 ? 'it' : 'them'}
                  </summary>
                  <ul className="mt-1 max-h-48 list-disc overflow-y-auto pl-5 font-mono">
                    {orphanPaths.map((p) => (
                      <li key={p} className="truncate" title={p}>
                        {p}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {autoSubmit?.lastRunAt ? (
                <p className="text-muted-foreground mb-2 text-xs">
                  Last auto-run {new Date(autoSubmit.lastRunAt).toLocaleString()}
                  {typeof autoSubmit.urlsInSitemap === 'number'
                    ? ` · ${autoSubmit.urlsInSitemap} URLs in sitemap`
                    : ''}
                  {autoSubmit.lastSitemapSubmitAt
                    ? ` · sitemap → Google ${autoSubmit.lastSitemapSubmitOk ? '✓' : '✗'} ${new Date(autoSubmit.lastSitemapSubmitAt).toLocaleString()}`
                    : ''}
                  {autoSubmit.lastIndexNowAt
                    ? ` · IndexNow ${autoSubmit.lastIndexNowOk ? '✓' : '✗'} ${autoSubmit.lastIndexNowUrls ?? 0} URLs`
                    : ''}
                  {autoSubmit.lastBingSubmitAt
                    ? ` · Bing submit ${autoSubmit.lastBingSubmitOk ? '✓' : '✗'} ${autoSubmit.lastBingSubmitUrls ?? 0} URLs ${new Date(autoSubmit.lastBingSubmitAt).toLocaleString()}`
                    : showBingCol
                      ? ' · Bing: nothing submitted yet'
                      : ''}
                  {autoSubmit.linkScanCoverage
                    ? ` · links scanned on ${autoSubmit.linkScanCoverage.scanned}/${autoSubmit.linkScanCoverage.total} pages`
                    : ''}
                  {autoSubmit.lastError ? (
                    <span className="text-red-600"> · {autoSubmit.lastError}</span>
                  ) : null}
                  {autoSubmit.lastBingError ? (
                    <span className="text-red-600"> · Bing: {autoSubmit.lastBingError}</span>
                  ) : null}
                </p>
              ) : (
                <p className="text-muted-foreground mb-2 text-xs">
                  Auto-submit has not run yet — it runs within the hour.
                </p>
              )}
              {indexQueue.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nothing waiting — all discovered URLs are indexed. New pages/products will appear
                  here automatically.
                </p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="pb-2 pr-4">URL</th>
                      {showGoogleCol && <th className="pb-2 pr-4">Google</th>}
                      {showBingCol && <th className="pb-2 pr-4">Bing</th>}
                      <th className="pb-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indexQueue.slice(0, 25).map((row: any) => (
                      <tr key={row.url} className="border-t align-top">
                        <td className="max-w-[280px] truncate py-1.5 pr-4" title={row.url}>
                          {row.url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                          {row.orphan ? (
                            <span
                              className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                              title="No other page on the site links to this URL"
                            >
                              orphan
                            </span>
                          ) : null}
                        </td>
                        {showGoogleCol && (
                          <td className="py-1.5 pr-4">
                            <EngineCell
                              state={
                                row.engines?.google ?? {
                                  status: legacyGoogleStatus(row),
                                  lastCheckedAt: row.lastInspectedAt,
                                  lastSubmittedAt: row.submittedAt,
                                }
                              }
                              submitLabel="Sitemap submitted"
                            />
                          </td>
                        )}
                        {showBingCol && (
                          <td className="py-1.5 pr-4">
                            <EngineCell
                              state={row.engines?.bing ?? { status: 'unknown' }}
                              submitLabel="Submitted"
                            />
                          </td>
                        )}
                        <td className="text-muted-foreground py-1.5 text-xs">
                          <div>
                            {row.error ||
                              row.coverageState ||
                              row.engines?.bing?.detail ||
                              'Not yet checked'}
                          </div>
                          <div className="text-[11px]">
                            {row.indexNowAt
                              ? `IndexNow ${row.indexNowStatus === 'sent' ? 'sent' : 'failed'} ${new Date(row.indexNowAt).toLocaleDateString()}`
                              : 'IndexNow not yet pinged'}
                            {typeof row.inboundLinks === 'number'
                              ? ` · ${row.inboundLinks} internal link${row.inboundLinks === 1 ? '' : 's'}`
                              : ''}
                          </div>
                          {row.remedy && (
                            <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                              → {row.remedy}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── 2. Directory recommendations (Pro+) ─────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Recommended directories</h2>
          {!isPro && <UpgradeChip to="pro" />}
        </div>

        {!isPro && (
          <div className="text-muted-foreground text-sm">
            See the directories that drive the most leads + SEO authority for businesses like yours.{' '}
            <Link
              to={`/projects/${projectId}/plugins/seo-copilot?tab=plans`}
              className="text-primary underline"
            >
              Upgrade to Pro →
            </Link>
          </div>
        )}

        {isPro && recs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left text-xs uppercase">
                <tr>
                  <th className="pb-2 pr-4">Directory</th>
                  <th className="pb-2 pr-4">Value</th>
                  <th className="pb-2 pr-4">Pricing</th>
                  <th className="pb-2 pr-4">Why</th>
                  <th className="pb-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((rec: any) => (
                  <tr key={rec.id} className="border-t">
                    <td className="py-2 pr-4 font-medium">{rec.name}</td>
                    <td className="py-2 pr-4">
                      <span
                        className={
                          rec.value === 'high'
                            ? 'text-green-600'
                            : rec.value === 'medium'
                              ? 'text-amber-600'
                              : 'text-muted-foreground'
                        }
                      >
                        {rec.value}
                      </span>
                    </td>
                    <td className="py-2 pr-4 capitalize">{rec.pricing}</td>
                    <td className="text-muted-foreground py-2 pr-4">{rec.rationale}</td>
                    <td className="py-2">
                      <a
                        href={rec.addUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary inline-flex items-center gap-1 underline"
                      >
                        Add <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Blog drafts are generated from the Blog plugin (sectioned HTML +
          images), not here — the old SEO Co-Pilot markdown draft was removed. */}

      {/* ── 4. GBP + Outreach pointers ────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <h2 className="mb-3 text-lg font-medium">More tools</h2>
        <div className="text-muted-foreground space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <ChevronRight className="h-3.5 w-3.5" />
            <span>
              <strong>Google Business Profile wizard</strong> — connect your GBP from the API tab.
              {!isPro && ' Requires Pro.'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ChevronRight className="h-3.5 w-3.5" />
            <span>
              <strong>Backlink outreach drafts</strong> — POST a prospect via API, generate the
              draft, send manually.
              {!isBusiness && ' Requires Business.'}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
